// Quality-Specific Movement Training.
// Source: Modern Basketball Blueprint, pages 31-39.
// Three parts: the frame, the trained eye, the 7-quality deck.
// Part 3 uses the new "deck" block — one sub-card per quality with
// Next/Previous so coaches click through them one at a time.
// Voice lifted from the book, lightly cleaned, no em dashes anywhere.

export const QUALITY_SPECIFIC_MOVEMENT_LESSON = {
  key: "quality-specific-movement-training",
  kind: "lesson",
  title: "Quality-Specific Movement Training",
  sub: "Why your players' skill plateaus are usually movement plateaus, and the seven qualities to look for first.",
  chapters: [
    {
      id: "frame",
      title: "Basketball skills are movement skills",
      blocks: [
        { kind: "text", md:
          "**All basketball skills are dependent on movement skills.**" },
        { kind: "text", md:
          "Most player development treats skills training and athletic performance as two separate jobs. Skills coaches handle the on-court work. Performance coaches handle the weight room. The two rarely talk." },
        { kind: "text", md:
          "That separation is the gap where most players plateau." },
        { kind: "text", md:
          "Quality-specific movement training closes the gap. Same coach, same session, evaluating every player through both lenses at once:\n\n- The skill lens: can they execute what the game demands?\n- The movement lens: can their body do what the skill requires?" },
        { kind: "text", md:
          "When the skill lens doesn't explain the plateau, the movement lens usually does." },
        { kind: "text", md:
          "**Why this matters even if you have a good performance coach on staff:**\n\n- You see the athlete play more than any performance coach ever will. You evaluate movement in the context of basketball, which is the only context that matters.\n- You can connect a physical quality to an on-court skill in a way that builds athlete buy-in. They feel the work pay off in their game, not just in their squat numbers.\n- Most athletes don't have consistent access to a good performance coach. If you don't address it, nobody will." },
      ],
    },

    {
      id: "trained-eye",
      title: "How to see what's missing",
      blocks: [
        { kind: "text", md:
          "The first skill is the eye. You look at a player move and you see WHY they can't do the thing." },
        { kind: "text", md:
          "Old heads already do this on a rudimentary level. Their language sounds non-technical but it's usually pointing at the right underlying quality:" },
        { kind: "text", md:
          "**\"Heavy feet, can't stay in front of nobody.\"**\nLateral ankle stiffness and eccentric rate of force development. They can't absorb force and pop off the ground laterally." },
        { kind: "text", md:
          "**\"You need to get low, man.\"**\nThey need to get stronger at high speeds in lower positions." },
        { kind: "text", md:
          "**\"He moves like a robot.\"**\nNo coordination, no toolbox of movement solutions, no fluidity." },
        { kind: "text", md:
          "Don't underestimate what you already notice. Take it one step further and ask what those age-old observations actually mean." },
        { kind: "text", md:
          "**Work backwards from skills, too.** Sometimes the path is sport-specific skill, then underlying movement quality." },
        { kind: "text", md:
          "Player has a hitch in their jumper that won't smooth out. Could be limited shoulder mobility (delaying the upward motion of the ball). Could be poor energy transfer between body segments. Could be ankle stiffness slowing the jump while the upper body races ahead. Could be raw strength." },
        { kind: "text", md:
          "Player can't attack ball screens cleanly, turns the ball over, misses chances to snake or split. Could be poor lateral change of direction (can't split a screen in a fraction of a second). Could be spatial awareness (avoids tight spaces because they don't read the gap)." },
        { kind: "text", md:
          "The point isn't to become a biomechanist. The point is to ask \"what's the underlying movement quality holding this back?\" and have a tool for it." },
      ],
    },

    {
      id: "toolbox",
      title: "The 7-quality starter toolbox",
      blocks: [
        { kind: "text", md:
          "You don't need a degree in biomechanics. You need a trained eye, curiosity, and the willingness to address movement inefficiencies with intention." },
        { kind: "text", md:
          "Seven common qualities to look for. Click through each one. For each: what to look for, what it means, and practical ways to address it in your sessions." },

        { kind: "deck", cards: [
          {
            title: "1. Operating at Deep Joint Angles",
            blocks: [
              { kind: "text", md:
                "*Look for:* players who struggle to bend or squat deeply at game speed. Play too upright defensively, can't explode out of drives, lose balance in contact." },
              { kind: "text", md:
                "*Means:* lacking dynamic strength, stability, or mobility in low positions." },
              { kind: "text", md:
                "*Fix:*\n\n- Ball rolls or deep squat reaches in the warmup. Build comfort in lower positions.\n- Low squat walks and deep-tier plyometrics. Move fast and powerfully with a lower center of gravity.\n- General mobility flows and joint capsule work. Open up hips, knees, ankles." },
            ],
          },
          {
            title: "2. Upper/Lower Body Dissociation",
            blocks: [
              { kind: "text", md:
                "*Look for:* players who move as one rigid unit. Upper and lower body turn together instead of separating fluidly. Can't flip the hips smoothly on defense or transition between slides and sprints." },
              { kind: "text", md:
                "*Means:* lacking rotational control, mobility, and sequencing between upper and lower body." },
              { kind: "text", md:
                "*Fix:*\n\n- Hip-flip work. Rotate at the hips while the upper body stays fixed.\n- Defensive drills with the ball overhead. Forces the torso and hips to work independently.\n- Rotational warmups and med ball throws. Train dissociation dynamically." },
            ],
          },
          {
            title: "3. Elasticity & Ankle Stiffness",
            blocks: [
              { kind: "text", md:
                "*Look for:* \"heavy-footed\" players. No pop off the floor. Slow off the ground on finishes and rebounds." },
              { kind: "text", md:
                "*Means:* limited elastic strength or ankle stiffness. Can't store and release energy efficiently." },
              { kind: "text", md:
                "*Fix:*\n\n- Extensive plyometrics: pogo hops, line hops, skipping. Teach quick elastic response.\n- Single-leg bounding and hopping. Develop reactive strength and coordination.\n- Explosive finishing drills with longer rest. Incentivize jump quickness, not just jump height." },
            ],
          },
          {
            title: "4. General Change of Direction Ability",
            blocks: [
              { kind: "text", md:
                "*Look for:* players who struggle to powerfully exit cuts, take little choppy steps before they reaccelerate." },
              { kind: "text", md:
                "*Means:* needs stronger eccentric control (absorbing force), efficient posture, and reactivity when changing direction." },
              { kind: "text", md:
                "*Fix:*\n\n- Lateral bounds and crossover hops. Train power moving side to side.\n- Mirror drills or tag games. Build reactive agility with the competitive juices flowing.\n- Sharp stop-and-go challenges. Teach decelerating and reaccelerating efficiently." },
            ],
          },
          {
            title: "5. Proprioception & Body Awareness",
            blocks: [
              { kind: "text", md:
                "*Look for:* players who seem clumsy. Limited awareness of where their limbs and body are in space." },
              { kind: "text", md:
                "*Means:* sensory feedback and coordination systems are underdeveloped. Hurts balance, timing, fluidity." },
              { kind: "text", md:
                "*Fix:*\n\n- Eyes-closed balance holds and single-leg stands. Challenge sensory awareness.\n- Eyes-closed movement patterns (hop variations, freestyle dribbling). Develop trust in internal feedback.\n- Controlled body control work: rolling, crawling, tumbling. Improve coordination across the kinetic chain." },
            ],
          },
          {
            title: "6. Horizontal Power & Acceleration",
            blocks: [
              { kind: "text", md:
                "*Look for:* players who can't explode powerfully with an exaggerated forward lean in their first three steps." },
              { kind: "text", md:
                "*Means:* lack horizontal force production and dynamic posture control. Especially weak from unorthodox starting positions." },
              { kind: "text", md:
                "*Fix:*\n\n- Hip hinge patterning, horizontal med ball throws, loaded lean starts. Teach forward projection.\n- Partner-resisted sprints (hands on shoulders). Strengthen the drive phase.\n- Short accelerations from different positions: kneeling, split stance, push-up start, sitting. Train first-step power from any angle." },
            ],
          },
          {
            title: "7. Basketball Strength (Contact and Close-Quarters)",
            blocks: [
              { kind: "text", md:
                "*Look for:* players who get bumped off balance easily, struggle in traffic, avoid contact when finishing, rebounding, or fighting for the ball." },
              { kind: "text", md:
                "*Means:* weight room strength alone won't fix this. They need strength relative to other moving, dynamic humans." },
              { kind: "text", md:
                "*Fix:*\n\n- Manual resistance drills (shoulder pushes, sumo stance battles). Build core and postural strength in real contact.\n- Shoulder-to-shoulder lateral battles. Teach absorbing and redirecting force.\n- Constraint-based contact finishing drills. Help players stay composed and strong through contact." },
            ],
          },
        ]},

        { kind: "text", md:
          "**Where to start.**" },
        { kind: "text", md:
          "Don't try to hit every quality at once. Pick 2-3 athletes. Identify the most apparent quality they lack. Integrate 3-5 minutes of targeted work into warmups or early in the session." },
        { kind: "text", md:
          "Over time you build the eye, and creative drills that match what you see start emerging naturally." },
      ],
    },
  ],
};
